import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import Typography from '@components/common/typography';
import { Icon } from '@components/icons';
import Status from '@components/common/status';
import Info from '@components/common/info';
import Button from '@components/common/button';
import { apiPayoutService, Payout, PayoutApiError, PayoutStatus } from '@api';
import { useUserContext } from '@hooks/context/useUserContext';
import { useEnvironmentContext } from '@hooks/context/useEnvironmentContext';
import DevPageHeader from '../_shared/PageHeader';
import styles from './styles.module.scss';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 30;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTs(iso: string): string {
    return new Date(iso).toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

function formatAmount(cents: number, currency: string): string {
    return `${(cents / 100).toFixed(2)} ${currency}`;
}

function truncateId(id: string): string {
    if (id.length <= 14) return id;
    return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

function maskCustomer(scopedId: string): string {
    if (scopedId.length <= 10) return scopedId;
    return `${scopedId.slice(0, 6)}…${scopedId.slice(-4)}`;
}

function payoutStatusVariant(status: PayoutStatus): 'success' | 'gray' | 'error' {
    if (status === 'succeeded') return 'success';
    if (status === 'failed') return 'error';
    return 'gray';
}

function payoutStatusLabel(status: PayoutStatus): string {
    return status.charAt(0).toUpperCase() + status.slice(1);
}

function chipClass(s: PayoutStatus, isActive: boolean): string {
    if (!isActive) return styles.status_chip;
    const map: Record<PayoutStatus, string> = {
        succeeded: styles.chip_succeeded,
        pending: styles.chip_pending,
        failed: styles.chip_failed
    };
    return `${styles.status_chip} ${map[s]}`;
}

// ─── Filter types ─────────────────────────────────────────────────────────────

interface PayoutsFilter {
    date_from: string;
    date_to: string;
    status: PayoutStatus[];
}

const EMPTY_FILTER: PayoutsFilter = { date_from: '', date_to: '', status: [] };

const STATUS_OPTIONS: PayoutStatus[] = ['pending', 'succeeded', 'failed'];

// ─── Layout ───────────────────────────────────────────────────────────────────

const DevelopersPayoutsLayout = () => {
    const { user } = useUserContext();
    const { env } = useEnvironmentContext();
    const [filter, setFilter] = useState<PayoutsFilter>(EMPTY_FILTER);
    const [payouts, setPayouts] = useState<Payout[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoadError, setHasLoadError] = useState(false);
    const [trigger, setTrigger] = useState(0);
    const loaderRef = useRef<HTMLDivElement>(null);
    const pageRef = useRef(1);
    const filterRef = useRef<PayoutsFilter>(EMPTY_FILTER);

    const stats = useMemo(() => {
        const succeeded = payouts.filter((p) => p.status === 'succeeded');
        const failedCount = payouts.filter((p) => p.status === 'failed').length;
        const currency = payouts[0]?.currency ?? 'CAD';
        const totalCents = succeeded.reduce((acc, p) => acc + p.amount_cents, 0);
        return {
            totalDisplay: payouts.length > 0 ? formatAmount(totalCents, currency) : '–',
            succeededCount: succeeded.length,
            failedCount
        };
    }, [payouts]);

    const fetchPayouts = useCallback(
        async (pageNum: number, f: PayoutsFilter, append: boolean) => {
            setIsLoading(true);
            setHasLoadError(false);
            try {
                const res = await apiPayoutService.list({
                    page: pageNum,
                    page_size: PAGE_SIZE,
                    date_from: f.date_from || undefined,
                    date_to: f.date_to || undefined,
                    status: f.status.length > 0 ? f.status : undefined,
                    environment: env
                });
                setPayouts((prev) => (append ? [...prev, ...res.payouts] : res.payouts));
                setHasMore(res.has_more ?? false);
                pageRef.current = pageNum;
            } catch (err) {
                // Graceful degradation: the service's own contract says a 404/501
                // means the backend endpoint isn't deployed yet, so treat that
                // case as an empty list. Any other failure (network, 5xx, auth)
                // is a real error and must render as one — it must not silently
                // collapse into the same "no payouts yet" empty state.
                const isNotDeployedYet =
                    err instanceof PayoutApiError && (err.status === 404 || err.status === 501);
                if (!isNotDeployedYet) setHasLoadError(true);
                if (!append) setPayouts([]);
                setHasMore(false);
            } finally {
                setIsLoading(false);
            }
        },
        [env]
    );

    const applyFilter = useCallback(
        (f: PayoutsFilter) => {
            filterRef.current = f;
            pageRef.current = 1;
            void fetchPayouts(1, f, false);
        },
        [fetchPayouts]
    );

    // Initial fetch once the user context resolves, and re-fetch whenever
    // the Sandbox/Live pill switches.
    useEffect(() => {
        if (user) {
            void fetchPayouts(1, EMPTY_FILTER, false);
        }
    }, [user, env]);

    // IntersectionObserver — increment trigger when the sentinel scrolls into view
    const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
        const [entry] = entries;
        if (entry.isIntersecting) setTrigger((prev) => prev + 1);
    }, []);

    useEffect(() => {
        const option = { root: null, rootMargin: '20px', threshold: 0 };
        const observer = new IntersectionObserver(handleObserver, option);
        if (loaderRef.current) observer.observe(loaderRef.current);
    }, [handleObserver]);

    // Load next page when the sentinel fires
    useEffect(() => {
        if (hasMore && !isLoading) {
            const nextPage = pageRef.current + 1;
            void fetchPayouts(nextPage, filterRef.current, true);
        }
    }, [trigger]);

    const toggleStatus = useCallback(
        (s: PayoutStatus) => {
            const next = filter.status.includes(s)
                ? filter.status.filter((x) => x !== s)
                : [...filter.status, s];
            const newFilter = { ...filter, status: next };
            setFilter(newFilter);
            applyFilter(newFilter);
        },
        [filter, applyFilter]
    );

    const handleApplyDates = useCallback(() => {
        applyFilter(filter);
    }, [filter, applyFilter]);

    const handleClear = useCallback(() => {
        setFilter(EMPTY_FILTER);
        applyFilter(EMPTY_FILTER);
    }, [applyFilter]);

    const hasActiveFilter =
        filter.date_from !== '' || filter.date_to !== '' || filter.status.length > 0;

    const statCards = useMemo(
        () => [
            {
                title: 'Total paid out',
                helper: 'Sum of succeeded payout amounts in the current view',
                value: stats.totalDisplay,
                style: styles.green,
                icon: 'money' as const
            },
            {
                title: 'Succeeded',
                helper: 'Count of succeeded payouts in the current view',
                value: String(stats.succeededCount),
                style: styles.total,
                icon: 'check_outlined' as const
            },
            {
                title: 'Failed',
                helper: 'Count of failed payouts in the current view',
                value: String(stats.failedCount),
                style: styles.pending,
                icon: 'error_outlined' as const
            }
        ],
        [stats]
    );

    return (
        <motion.div {...pageAnimation}>
            <div className={styles.wrapper}>
                <DevPageHeader
                    title="Payouts"
                    subtitle="Track payouts sent to your customers via the XEPPT Payouts API."
                />

                {/* Capability banner */}
                <div className={styles.banner}>
                    <Icon name="info" width={18} height={18} className={styles.banner_icon} />
                    <Typography variant="body4" className={styles.banner_text}>
                        <strong>Live payouts require MLRO approval.</strong> Test-mode payouts are
                        active. This view is read-only — payouts are initiated from your backend
                        via the Payouts API.
                    </Typography>
                </div>

                {/* Stats row */}
                <div className={styles.points_wrapper_wrapper}>
                    <div className={styles.points_wrapper}>
                        {statCards.map((card) => (
                            <div
                                key={card.title}
                                className={`${styles.point_wrapper} ${card.style}`}>
                                <div className={styles.icon}>
                                    <Icon name={card.icon} />
                                </div>
                                <div className={styles.text_wrapper}>
                                    <div className={styles.stat_title}>
                                        <Typography variant="body4">{card.title}</Typography>
                                        <Info
                                            variant="left"
                                            content={<>{card.helper}</>}
                                        />
                                    </div>
                                    <Typography variant="h3" className={styles.amount}>
                                        {card.value}
                                    </Typography>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Filter bar */}
                <div className={styles.filter_bar}>
                    <div className={styles.filter_dates}>
                        <div className={styles.filter_field}>
                            <label htmlFor="payout-date-from">From</label>
                            <input
                                id="payout-date-from"
                                type="date"
                                value={filter.date_from}
                                onChange={(e) =>
                                    setFilter((f) => ({ ...f, date_from: e.target.value }))
                                }
                            />
                        </div>
                        <div className={styles.filter_field}>
                            <label htmlFor="payout-date-to">To</label>
                            <input
                                id="payout-date-to"
                                type="date"
                                value={filter.date_to}
                                onChange={(e) =>
                                    setFilter((f) => ({ ...f, date_to: e.target.value }))
                                }
                            />
                        </div>
                        <button
                            className={styles.filter_apply}
                            onClick={handleApplyDates}
                            disabled={isLoading}>
                            Apply
                        </button>
                    </div>
                    <div className={styles.filter_statuses}>
                        {STATUS_OPTIONS.map((s) => (
                            <button
                                key={s}
                                className={chipClass(s, filter.status.includes(s))}
                                onClick={() => toggleStatus(s)}>
                                {payoutStatusLabel(s)}
                            </button>
                        ))}
                    </div>
                    {hasActiveFilter && (
                        <button className={styles.filter_clear} onClick={handleClear}>
                            Clear filters
                        </button>
                    )}
                </div>

                {/* Payout list */}
                <div className={styles.table_wrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Payout ID</th>
                                <th>Customer</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && payouts.length === 0 &&
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={`skel-${i}`} className={styles.skeleton_row}>
                                        <td colSpan={5}>
                                            <div className={styles.skeleton_line} />
                                        </td>
                                    </tr>
                                ))
                            }
                            {!isLoading && payouts.length === 0 && hasLoadError && (
                                <tr>
                                    <td colSpan={5}>
                                        <div className={styles.empty}>
                                            <div className={styles.empty_icon}>
                                                <Icon
                                                    name="error_outlined"
                                                    width={26}
                                                    height={26}
                                                />
                                            </div>
                                            <Typography variant="body3" weight="semibold">
                                                Failed to load payouts
                                            </Typography>
                                            <Typography
                                                variant="body4"
                                                className={styles.empty_sub}>
                                                There was a problem fetching your payouts.
                                            </Typography>
                                            <Button
                                                variant="outlined-primary"
                                                size="small"
                                                onClick={() => applyFilter(filter)}>
                                                Retry
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {!isLoading && payouts.length === 0 && !hasLoadError && (
                                <tr>
                                    <td colSpan={5}>
                                        <div className={styles.empty}>
                                            <div className={styles.empty_icon}>
                                                <Icon
                                                    name="money_out"
                                                    width={26}
                                                    height={26}
                                                />
                                            </div>
                                            <Typography variant="body3" weight="semibold">
                                                No payouts yet
                                            </Typography>
                                            <Typography
                                                variant="body4"
                                                className={styles.empty_sub}>
                                                Send your first payout via the API to see it
                                                here.
                                            </Typography>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {payouts.map((payout) => (
                                <tr key={payout.id}>
                                    <td>
                                        <span className={styles.mono}>
                                            {truncateId(payout.id)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={styles.mono}>
                                            {maskCustomer(payout.scoped_id)}
                                        </span>
                                    </td>
                                    <td>
                                        {formatAmount(payout.amount_cents, payout.currency)}
                                    </td>
                                    <td>
                                        <Status
                                            variant={payoutStatusVariant(payout.status)}>
                                            {payoutStatusLabel(payout.status)}
                                        </Status>
                                    </td>
                                    <td>{formatTs(payout.created_at)}</td>
                                </tr>
                            ))}
                            {isLoading && payouts.length > 0 &&
                                Array.from({ length: 3 }).map((_, i) => (
                                    <tr key={`skel-more-${i}`} className={styles.skeleton_row}>
                                        <td colSpan={5}>
                                            <div className={styles.skeleton_line} />
                                        </td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>

                {/* Infinite scroll sentinel */}
                <div ref={loaderRef} />
            </div>
        </motion.div>
    );
};

export default DevelopersPayoutsLayout;
